import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, Zap, DollarSign, Trophy, Target, Activity, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import CryptoIcon from '../components/CryptoIcon';
import CryptoTicker from '../components/CryptoTicker';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
import TradeDetailModal from '../components/TradeDetailModal';

const API_URL = '';

// Comprehensive crypto and gold pairs list (40+ pairs)
const CRYPTO_PAIRS = [
  { symbol: 'BTC/USDT', name: 'Bitcoin', icon: '₿', color: '#F7931A' },
  { symbol: 'ETH/USDT', name: 'Ethereum', icon: 'Ξ', color: '#627EEA' },
  { symbol: 'BNB/USDT', name: 'Binance Coin', icon: 'BNB', color: '#F3BA2F' },
  { symbol: 'SOL/USDT', name: 'Solana', icon: 'SOL', color: '#9945FF' },
  { symbol: 'XRP/USDT', name: 'Ripple', icon: 'XRP', color: '#00AAE4' },
  { symbol: 'DOGE/USDT', name: 'Dogecoin', icon: 'Ð', color: '#C3A634' },
  { symbol: 'ADA/USDT', name: 'Cardano', icon: 'ADA', color: '#0033AD' },
  { symbol: 'AVAX/USDT', name: 'Avalanche', icon: 'AVAX', color: '#E84142' },
  { symbol: 'DOT/USDT', name: 'Polkadot', icon: 'DOT', color: '#E6007A' },
  { symbol: 'MATIC/USDT', name: 'Polygon', icon: 'MATIC', color: '#8247E5' },
  { symbol: 'LTC/USDT', name: 'Litecoin', icon: 'Ł', color: '#345D9D' },
  { symbol: 'LINK/USDT', name: 'Chainlink', icon: 'LINK', color: '#2A5ADA' },
  { symbol: 'UNI/USDT', name: 'Uniswap', icon: 'UNI', color: '#FF007A' },
  { symbol: 'ATOM/USDT', name: 'Cosmos', icon: 'ATOM', color: '#2E3148' },
  { symbol: 'TRX/USDT', name: 'Tron', icon: 'TRX', color: '#FF060A' },
  { symbol: 'TON/USDT', name: 'Toncoin', icon: 'TON', color: '#0088CC' },
  { symbol: 'USDC/USDT', name: 'USD Coin', icon: 'USDC', color: '#2775CA' },
  { symbol: 'SHIB/USDT', name: 'Shiba Inu', icon: 'SHIB', color: '#FFA409' },
  { symbol: 'APT/USDT', name: 'Aptos', icon: 'APT', color: '#00D4AA' },
  { symbol: 'ARB/USDT', name: 'Arbitrum', icon: 'ARB', color: '#28A0F0' },
  { symbol: 'NEAR/USDT', name: 'Near Protocol', icon: 'NEAR', color: '#00C08B' },
  { symbol: 'FTM/USDT', name: 'Fantom', icon: 'FTM', color: '#13B5EC' },
  { symbol: 'ALGO/USDT', name: 'Algorand', icon: 'ALGO', color: '#000000' },
  { symbol: 'VET/USDT', name: 'VeChain', icon: 'VET', color: '#15BDFF' },
  { symbol: 'ICP/USDT', name: 'Internet Computer', icon: 'ICP', color: '#29ABE2' },
  { symbol: 'FIL/USDT', name: 'Filecoin', icon: 'FIL', color: '#0090FF' },
  { symbol: 'AAVE/USDT', name: 'Aave', icon: 'AAVE', color: '#B6509E' },
  { symbol: 'ETC/USDT', name: 'Ethereum Classic', icon: 'ETC', color: '#328332' },
  { symbol: 'XLM/USDT', name: 'Stellar', icon: 'XLM', color: '#000000' },
  { symbol: 'HBAR/USDT', name: 'Hedera', icon: 'HBAR', color: '#000000' },
  { symbol: 'MANA/USDT', name: 'Decentraland', icon: 'MANA', color: '#FF2D55' },
  { symbol: 'SAND/USDT', name: 'The Sandbox', icon: 'SAND', color: '#00ADEF' },
  { symbol: 'AXS/USDT', name: 'Axie Infinity', icon: 'AXS', color: '#0055D5' },
  { symbol: 'THETA/USDT', name: 'Theta Network', icon: 'THETA', color: '#2AB8E6' },
  { symbol: 'EOS/USDT', name: 'EOS', icon: 'EOS', color: '#000000' },
  // Gold Trading Pairs
  { symbol: 'GOLD/USDT', name: 'Gold', icon: '🏆', color: '#FFD700', isGold: true },
  { symbol: 'GOLD/BTC', name: 'Gold vs Bitcoin', icon: '🏆', color: '#FFD700', isGold: true },
  { symbol: 'GOLD/ETH', name: 'Gold vs Ethereum', icon: '🏆', color: '#FFD700', isGold: true },
  { symbol: 'GOLD/EUR', name: 'Gold vs Euro', icon: '🏆', color: '#FFD700', isGold: true },
  { symbol: 'GOLD/GBP', name: 'Gold vs British Pound', icon: '🏆', color: '#FFD700', isGold: true },
];

const BinaryTrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { balanceVisible, toggleBalanceVisibility, formatBalance } = useBalanceVisibility();
  
  // State management
  const [selectedPair, setSelectedPair] = useState(CRYPTO_PAIRS[0]);
  const [showPairDropdown, setShowPairDropdown] = useState(false);
  const [durations, setDurations] = useState([]);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [tradeAmount, setTradeAmount] = useState('');
  const [balance, setBalance] = useState(null);
  const [activeTrades, setActiveTrades] = useState([]);
  const [completedTrades, setCompletedTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('trade'); // 'trade', 'active', 'history', 'stats'
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);
  
  // Track if fetch is in progress (prevent concurrent API calls)
  const fetchInProgressRef = React.useRef(false);

  // Set selected pair from navigation state if provided
  useEffect(() => {
    if (location.state?.selectedPair) {
      const pairSymbol = location.state.selectedPair;
      const pair = CRYPTO_PAIRS.find(p => p.symbol === pairSymbol);
      if (pair) {
        setSelectedPair(pair);
      }
    }
  }, [location.state]);

  // Fetch static duration configuration ONCE on mount
  const fetchDurations = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const durationsRes = await axios.get(`${API_URL}/api/binary/durations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (durationsRes.data.success) {
        const fetchedDurations = durationsRes.data.durations;
        setDurations(fetchedDurations);
        
        // Set default selection on first load
        if (fetchedDurations.length > 0 && !selectedDuration) {
          setSelectedDuration(fetchedDurations[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching durations:', error);
    }
  };

  // Fetch dynamic data (balance, trades, stats) that changes frequently
  const fetchDynamicData = async () => {
    // Prevent concurrent API calls (debouncing)
    if (fetchInProgressRef.current) {
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchInProgressRef.current = true;
    
    try {
      // Fetch balance
      const balanceRes = await axios.get(`${API_URL}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (balanceRes.data.success) {
        setBalance(balanceRes.data.balance);
      }

      // Fetch active trades
      const activeRes = await axios.get(`${API_URL}/api/binary/active-trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (activeRes.data.success) {
        setActiveTrades(activeRes.data.trades);
      }

      // Fetch completed trades
      const completedRes = await axios.get(`${API_URL}/api/binary/completed-trades`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (completedRes.data.success) {
        setCompletedTrades(completedRes.data.trades);
      }

      // Fetch stats
      const statsRes = await axios.get(`${API_URL}/api/binary/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }
    } catch (error) {
      console.error('Error fetching dynamic data:', error);
    } finally {
      // Always release the lock, even if there's an error
      fetchInProgressRef.current = false;
    }
  };

  useEffect(() => {
    // Fetch static durations once on mount
    fetchDurations();
    
    // Fetch dynamic data on mount
    fetchDynamicData();
    
    // Refresh dynamic data every 5 seconds (NOT durations)
    const interval = setInterval(() => {
      fetchDynamicData();
    }, 5000);
    
    // Real-time countdown timer for active trades
    const countdownInterval = setInterval(() => {
      setActiveTrades(prevTrades => {
        // Filter out expired trades and trigger refetch
        const currentTime = new Date();
        const stillActive = prevTrades.filter(trade => {
          const expiryTime = new Date(trade.expiry_time);
          return expiryTime > currentTime;
        });
        
        // If any trades expired, refetch dynamic data to update UI
        if (stillActive.length < prevTrades.length) {
          fetchDynamicData();
        }
        
        return prevTrades; // Return original to trigger re-render
      });
    }, 1000); // Update every second
    
    return () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    };
  }, []);

  const handleTrade = async (direction) => {
    if (!tradeAmount || parseFloat(tradeAmount) <= 0) {
      toast({ 
        title: 'Error', 
        description: 'Please enter a valid trade amount', 
        variant: 'destructive' 
      });
      return;
    }

    if (!selectedDuration) {
      toast({ 
        title: 'Error', 
        description: 'Please select a trading duration', 
        variant: 'destructive' 
      });
      return;
    }

    // Validate amount against selected duration limits
    const amount = parseFloat(tradeAmount);
    if (amount < selectedDuration.min_amount) {
      toast({ 
        title: 'Amount Too Low', 
        description: `Minimum amount for ${selectedDuration.label} is $${selectedDuration.min_amount}`, 
        variant: 'destructive' 
      });
      return;
    }
    if (amount > selectedDuration.max_amount) {
      toast({ 
        title: 'Amount Too High', 
        description: `Maximum amount for ${selectedDuration.label} is $${selectedDuration.max_amount.toLocaleString()}`, 
        variant: 'destructive' 
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/binary/trade`,
        {
          pair: selectedPair.symbol,
          amount: parseFloat(tradeAmount),
          direction: direction,
          duration: selectedDuration.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast({
          title: '🎯 Trade Placed Successfully!',
          description: `${direction.toUpperCase()} trade for $${tradeAmount} on ${selectedPair.symbol}`
        });
        setTradeAmount('');
        fetchDynamicData();
      }
    } catch (error) {
      toast({
        title: 'Trade Failed',
        description: error.response?.data?.detail || 'Failed to place trade',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse duration string to seconds (fallback for old trades)
  const parseDurationToSeconds = (durationStr) => {
    if (!durationStr) return 0;
    
    // Check if it's already in seconds format (e.g., "30s", "60s")
    if (durationStr.endsWith('s')) {
      return parseInt(durationStr);
    }
    
    // Handle minute format (e.g., "2m", "5m")
    if (durationStr.endsWith('m')) {
      return parseInt(durationStr) * 60;
    }
    
    // Handle hour format (e.g., "1h")
    if (durationStr.endsWith('h')) {
      return parseInt(durationStr) * 3600;
    }
    
    return 0;
  };

  const getRemainingTime = (startTime, durationSeconds, durationStr) => {
    // If durationSeconds is missing, try to parse from duration string
    let actualDurationSeconds = durationSeconds;
    
    if (!actualDurationSeconds || isNaN(actualDurationSeconds) || actualDurationSeconds === 0) {
      actualDurationSeconds = parseDurationToSeconds(durationStr);
    }
    
    // Validation: check if parameters are valid
    if (!startTime || !actualDurationSeconds || isNaN(actualDurationSeconds)) {
      return "00:00";
    }
    
    const now = new Date();
    const start = new Date(startTime);
    
    // Check if date is valid
    if (isNaN(start.getTime())) {
      return "00:00";
    }
    
    // Calculate elapsed time since trade started
    const elapsedMs = now - start;
    const elapsedSeconds = Math.floor(elapsedMs / 1000);
    
    // Calculate remaining seconds from the original duration
    const remainingSeconds = Math.max(0, actualDurationSeconds - elapsedSeconds);
    
    // If expired, return "00:00"
    if (remainingSeconds === 0) return "00:00";
    
    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    
    // Format with leading zeros - always show consistent format
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen" style={{ 
      background: 'linear-gradient(135deg, #0a1628 0%, #0f2847 50%, #1a3a5c 100%)',
      paddingBottom: '80px'
    }}>
      {/* Crypto Ticker at the top */}
      <CryptoTicker position="top" autoScroll={true} />
      
      {/* Header */}
      <div className="sticky top-0 z-20" style={{ 
        background: 'linear-gradient(135deg, #0a1628 0%, #0f2847 100%)',
        borderBottom: '2px solid rgba(0, 200, 255, 0.3)',
        boxShadow: '0 4px 20px rgba(0, 200, 255, 0.1)'
      }}>
        <div className="flex items-center justify-between px-4 py-4">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-white hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center">
            <div className="font-bold text-xl text-white flex items-center justify-center gap-2">
              <Zap className="w-5 h-5 text-cyan-400" />
              Binary Trading
            </div>
            <div className="text-xs text-cyan-300">Lightning Fast Profits</div>
          </div>
          <div className="w-8"></div>
        </div>

        {/* Balance Display */}
        {balance && (
          <div className="px-4 pb-3">
            <div className="flex items-center justify-center gap-2 py-2 px-4 rounded-lg" style={{
              background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
              border: '2px solid rgba(0, 200, 255, 0.3)'
            }}>
              <DollarSign className="w-5 h-5 text-cyan-400" />
              <span className="text-sm text-gray-400">Balance:</span>
              <span className="text-lg font-bold text-white">
                ${formatBalance(balance.USDT)}
              </span>
              <button 
                onClick={toggleBalanceVisibility}
                className="ml-2 p-1 hover:bg-cyan-500/20 rounded transition-colors"
              >
                {balanceVisible ? (
                  <Eye className="w-4 h-4 text-cyan-400" />
                ) : (
                  <EyeOff className="w-4 h-4 text-cyan-400" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* View Tabs */}
        <div className="flex border-t border-cyan-800/30">
          {[
            { id: 'trade', label: 'Trade', icon: Zap },
            { id: 'active', label: 'Active', icon: Activity },
            { id: 'history', label: 'History', icon: Clock },
            { id: 'stats', label: 'Stats', icon: Trophy }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCurrentView(tab.id)}
              className={`flex-1 py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                currentView === tab.id
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-cyan-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-6">
        {currentView === 'trade' && (
          <div className="space-y-6">
            {/* Crypto Pair Selector */}
            <div>
              <label className="block text-sm font-semibold text-cyan-300 mb-2">Select Trading Pair</label>
              <div className="relative">
                <button
                  onClick={() => setShowPairDropdown(!showPairDropdown)}
                  className="w-full flex items-center justify-between p-4 rounded-xl transition-all"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
                    border: '2px solid rgba(0, 200, 255, 0.3)'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <CryptoIcon symbol={selectedPair.symbol.split('/')[0]} size={32} />
                    <div className="text-left">
                      <div className="font-bold text-white">{selectedPair.symbol}</div>
                      <div className="text-xs text-gray-400">{selectedPair.name}</div>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-cyan-400 transition-transform ${showPairDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown */}
                {showPairDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-2 max-h-80 overflow-y-auto rounded-xl z-30 bg-gradient-to-br from-cyan-600 via-cyan-700 to-blue-800 border-2 border-cyan-400/50 shadow-xl shadow-cyan-500/30">
                    {CRYPTO_PAIRS.map((pair, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          setSelectedPair(pair);
                          setShowPairDropdown(false);
                        }}
                        className={`w-full flex items-center justify-between gap-3 p-3 hover:bg-cyan-500/40 transition-colors border-b border-cyan-400/20 last:border-b-0 ${
                          pair.isGold ? 'bg-gradient-to-r from-yellow-600/20 to-yellow-700/20' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <CryptoIcon symbol={pair.symbol.split('/')[0]} size={32} />
                          <div className="text-left">
                            <div className="font-semibold text-white flex items-center gap-2">
                              {pair.symbol}
                              {pair.isGold && (
                                <span className="text-xs bg-yellow-500 text-black px-2 py-0.5 rounded font-bold">
                                  GOLD
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-white/80">{pair.name}</div>
                          </div>
                        </div>
                        {pair.isGold && (
                          <Trophy className="w-4 h-4 text-yellow-400" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Trading Durations Grid */}
            <div>
              <label className="block text-sm font-semibold text-cyan-300 mb-3">Select Duration & ROI</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                {durations.map((duration) => (
                  <button
                    key={duration.id}
                    onClick={() => {
                      console.log('═══════════════════════════════════════════════');
                      console.log('👆 [USER CLICK] Manual duration selection');
                      console.log('Time:', new Date().toISOString());
                      console.log('Clicked Duration:', {
                        id: duration.id,
                        label: duration.label,
                        duration: duration.duration
                      });
                      console.log('Previous Selection:', selectedDuration ? {
                        id: selectedDuration.id,
                        label: selectedDuration.label
                      } : 'NULL');
                      console.log('═══════════════════════════════════════════════');
                      setSelectedDuration(duration);
                    }}
                    className={`p-3 sm:p-4 rounded-xl transition-all transform hover:scale-105 ${
                      selectedDuration?.id === duration.id
                        ? 'ring-2 ring-cyan-400 shadow-lg'
                        : ''
                    }`}
                    style={{
                      background: selectedDuration?.id === duration.id
                        ? 'linear-gradient(135deg, rgba(0, 200, 255, 0.3) 0%, rgba(0, 150, 255, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 100, 150, 0.1) 100%)',
                      border: '2px solid rgba(0, 200, 255, 0.3)'
                    }}
                  >
                    <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-400" />
                      <span className="font-bold text-white text-sm sm:text-base">{duration.label}</span>
                    </div>
                    <div className="text-xl sm:text-2xl font-black text-cyan-400 mb-1">
                      {duration.roi_percentage}%
                    </div>
                    <div className="text-xs text-gray-400">ROI</div>
                    <div className="text-[10px] sm:text-xs text-gray-300 mt-1 sm:mt-2 font-semibold">
                      ${duration.min_amount.toLocaleString()} - ${duration.max_amount.toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Trade Amount Input */}
            <div>
              <label className="block text-sm font-semibold text-cyan-300 mb-2">Trade Amount (USDT)</label>
              <div className="relative">
                <input
                  type="number"
                  value={tradeAmount}
                  onChange={(e) => setTradeAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-4 py-4 rounded-xl text-white text-lg font-semibold bg-transparent focus:outline-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
                    border: '2px solid rgba(0, 200, 255, 0.3)'
                  }}
                />
                <DollarSign className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cyan-400" />
              </div>

              {/* Quick Amount Buttons */}
              <div className="grid grid-cols-4 gap-2 mt-3">
                {selectedDuration ? (
                  <>
                    <button
                      onClick={() => setTradeAmount(selectedDuration.min_amount.toString())}
                      className="py-2 rounded-lg font-semibold text-sm text-white transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                        border: '1px solid rgba(0, 200, 255, 0.3)'
                      }}
                    >
                      Min
                    </button>
                    <button
                      onClick={() => setTradeAmount((selectedDuration.max_amount * 0.25).toString())}
                      className="py-2 rounded-lg font-semibold text-sm text-white transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                        border: '1px solid rgba(0, 200, 255, 0.3)'
                      }}
                    >
                      25%
                    </button>
                    <button
                      onClick={() => setTradeAmount((selectedDuration.max_amount * 0.5).toString())}
                      className="py-2 rounded-lg font-semibold text-sm text-white transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                        border: '1px solid rgba(0, 200, 255, 0.3)'
                      }}
                    >
                      50%
                    </button>
                    <button
                      onClick={() => setTradeAmount(selectedDuration.max_amount.toString())}
                      className="py-2 rounded-lg font-semibold text-sm text-white transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                        border: '1px solid rgba(0, 200, 255, 0.3)'
                      }}
                    >
                      Max
                    </button>
                  </>
                ) : (
                  ['100', '500', '1000', '5000'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setTradeAmount(amount)}
                      className="py-2 rounded-lg font-semibold text-sm text-white transition-all hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                        border: '1px solid rgba(0, 200, 255, 0.3)'
                      }}
                    >
                      ${amount}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Trade Buttons */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <button
                onClick={() => handleTrade('up')}
                disabled={loading}
                className="py-6 rounded-xl font-black text-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)'
                }}
              >
                <TrendingUp className="w-8 h-8" />
                <span>UP</span>
              </button>
              <button
                onClick={() => handleTrade('down')}
                disabled={loading}
                className="py-6 rounded-xl font-black text-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4)'
                }}
              >
                <TrendingDown className="w-8 h-8" />
                <span>DOWN</span>
              </button>
            </div>
          </div>
        )}

        {/* Active Trades View */}
        {currentView === 'active' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-cyan-400 animate-pulse" />
              <h2 className="text-lg font-bold text-white">Active Trades</h2>
              <span className="ml-auto px-3 py-1 rounded-full bg-cyan-500/20 text-sm text-cyan-400 font-bold border border-cyan-500/30">
                {activeTrades.length} ongoing
              </span>
            </div>
            
            {activeTrades.length === 0 ? (
              <div className="text-center py-12 px-4 rounded-xl" style={{
                background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
                border: '2px solid rgba(0, 200, 255, 0.2)'
              }}>
                <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-2 font-semibold">No active trades</p>
                <p className="text-sm text-gray-500">Your active trades will appear here with live countdown</p>
                <button
                  onClick={() => setCurrentView('trade')}
                  className="mt-4 px-6 py-3 rounded-xl text-white font-bold shadow-lg shadow-cyan-500/30 hover:scale-105 transition-transform"
                  style={{
                    background: 'linear-gradient(135deg, #00d4ff 0%, #0088ff 100%)'
                  }}
                >
                  Start Trading
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {activeTrades.map((trade) => {
                  return (
                    <div
                      key={trade.trade_id}
                      onClick={() => {
                        setSelectedTrade(trade);
                        setShowTradeModal(true);
                      }}
                      className="p-4 rounded-xl border-2 cursor-pointer hover:scale-[1.02] transition-all shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.15) 0%, rgba(0, 150, 255, 0.15) 100%)',
                        borderColor: trade.direction === 'up' ? 'rgba(0, 255, 100, 0.4)' : 'rgba(255, 50, 50, 0.4)'
                      }}
                    >
                      {/* Header Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <CryptoIcon symbol={trade.pair.split('/')[0]} size={40} />
                          <div>
                            <div className="font-bold text-white text-lg">{trade.pair}</div>
                            <div className="text-xs text-gray-400 flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              {trade.duration}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold mb-1 ${
                            trade.direction === 'up' 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {trade.direction === 'up' ? (
                              <>
                                <TrendingUp className="w-4 h-4" />
                                UP
                              </>
                            ) : (
                              <>
                                <TrendingDown className="w-4 h-4" />
                                DOWN
                              </>
                            )}
                          </div>
                          <div className="text-xl font-bold text-white">${trade.amount}</div>
                        </div>
                      </div>

                      {/* Pending Status - Green Indicator with Period */}
                      <div className="flex items-center justify-center gap-3 py-4 px-4 rounded-xl mb-3" style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
                        border: '2px solid rgba(16, 185, 129, 0.4)'
                      }}>
                        {/* Green Rotating Loading Spinner */}
                        <div className="relative w-8 h-8">
                          <div className="absolute inset-0 border-4 border-green-500/30 rounded-full"></div>
                          <div className="absolute inset-0 border-4 border-transparent border-t-green-500 rounded-full animate-spin"></div>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-lg font-bold text-green-400">
                            Pending
                          </span>
                          <span className="text-2xl font-mono font-black text-green-300">
                            {trade.duration_seconds}s
                          </span>
                          <span className="text-xs text-gray-400">
                            period
                          </span>
                        </div>
                      </div>

                      {/* Trade Info Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-900/30 rounded-lg p-3">
                          <div className="text-xs text-gray-400 mb-1">Potential Win</div>
                          <div className="text-lg font-bold text-green-400">
                            +${((trade.amount * trade.roi_percentage) / 100).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">({trade.roi_percentage}% ROI)</div>
                        </div>
                        <div className="bg-slate-900/30 rounded-lg p-3">
                          <div className="text-xs text-gray-400 mb-1">Total Return</div>
                          <div className="text-lg font-bold text-white">
                            ${(trade.amount * (1 + trade.roi_percentage / 100)).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">If win</div>
                        </div>
                      </div>

                      {/* Click for Details */}
                      <div className="mt-3 text-center">
                        <span className="text-xs text-cyan-400 flex items-center justify-center gap-1">
                          <Target className="w-3 h-3" />
                          Tap for full details
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* History View */}
        {currentView === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-bold text-white">Trade History</h2>
              <span className="ml-auto px-3 py-1 rounded-full bg-slate-800/50 text-sm text-gray-400 font-bold border border-slate-700">
                {completedTrades.length} trades
              </span>
            </div>

            {completedTrades.length === 0 ? (
              <div className="text-center py-12 px-4 rounded-xl" style={{
                background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
                border: '2px solid rgba(0, 200, 255, 0.2)'
              }}>
                <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-2 font-semibold">No trade history yet</p>
                <p className="text-sm text-gray-500">Your completed trades will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {completedTrades.map((trade, index) => {
                  const isWin = trade.outcome === 'win';
                  const profitLoss = trade.profit || 0;
                  
                  return (
                    <div 
                      key={trade.trade_id || index} 
                      onClick={() => {
                        setSelectedTrade(trade);
                        setShowTradeModal(true);
                      }}
                      className="p-4 rounded-xl cursor-pointer hover:scale-[1.02] transition-all shadow-lg"
                      style={{
                        background: isWin
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
                          : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)',
                        border: `2px solid ${isWin ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`
                      }}
                    >
                      {/* Header Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <CryptoIcon symbol={trade.pair.split('/')[0]} size={36} />
                          <div>
                            <div className="font-bold text-white text-lg">{trade.pair}</div>
                            <div className="text-xs text-gray-400 flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              {trade.duration}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`px-3 py-1 rounded-full text-xs font-bold mb-1 ${
                            isWin 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {isWin ? '✓ WIN' : '✗ LOSS'}
                          </div>
                        </div>
                      </div>

                      {/* Direction & Amount */}
                      <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-700/50">
                        <div className="flex items-center gap-2">
                          <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${
                            trade.direction === 'up' 
                              ? 'bg-green-500/10 text-green-400' 
                              : 'bg-red-500/10 text-red-400'
                          }`}>
                            {trade.direction === 'up' ? (
                              <>
                                <TrendingUp className="w-3 h-3" />
                                UP
                              </>
                            ) : (
                              <>
                                <TrendingDown className="w-3 h-3" />
                                DOWN
                              </>
                            )}
                          </div>
                          <span className="text-sm text-gray-400">Investment:</span>
                          <span className="text-white font-bold">${trade.amount}</span>
                        </div>
                      </div>

                      {/* Profit/Loss Display - ENHANCED */}
                      <div className="mb-3">
                        {isWin ? (
                          <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg p-3 border border-green-500/40">
                            <div className="text-xs text-green-300 mb-1 font-semibold">🎉 Winning Amount</div>
                            <div className="text-2xl font-black text-green-400">
                              Won + ${profitLoss.toFixed(2)} USDT
                            </div>
                            <div className="text-xs text-green-300 mt-1">ROI: {trade.roi_percentage}%</div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-slate-900/30 rounded-lg p-2">
                              <div className="text-xs text-gray-400 mb-1">Loss</div>
                              <div className="text-lg font-bold text-red-400">
                                {profitLoss.toFixed(2)} USDT
                              </div>
                            </div>
                            <div className="bg-slate-900/30 rounded-lg p-2">
                              <div className="text-xs text-gray-400 mb-1">ROI</div>
                              <div className="text-lg font-bold text-cyan-400">
                                {trade.roi_percentage}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Trade Details */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>
                          {trade.start_time ? new Date(trade.start_time).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'N/A'}
                        </span>
                        <span className="text-cyan-400 flex items-center gap-1">
                          <Target className="w-3 h-3" />
                          View Details
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Stats View */}
        {currentView === 'stats' && stats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl text-center"
                   style={{
                     background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)',
                     border: '2px solid rgba(16, 185, 129, 0.3)'
                   }}>
                <div className="text-3xl font-black text-green-400">{stats.total_wins}</div>
                <div className="text-sm text-gray-400 mt-1">Total Wins</div>
              </div>
              <div className="p-4 rounded-xl text-center"
                   style={{
                     background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1) 100%)',
                     border: '2px solid rgba(239, 68, 68, 0.3)'
                   }}>
                <div className="text-3xl font-black text-red-400">{stats.total_losses}</div>
                <div className="text-sm text-gray-400 mt-1">Total Losses</div>
              </div>
            </div>

            <div className="p-6 rounded-xl text-center"
                 style={{
                   background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.2) 0%, rgba(0, 150, 255, 0.1) 100%)',
                   border: '2px solid rgba(0, 200, 255, 0.3)'
                 }}>
              <div className="text-5xl font-black text-cyan-400 mb-2">{stats.win_rate.toFixed(1)}%</div>
              <div className="text-sm text-gray-400">Win Rate</div>
            </div>

            <div className="p-6 rounded-xl text-center"
                 style={{
                   background: stats.total_profit >= 0
                     ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)'
                     : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.1) 100%)',
                   border: stats.total_profit >= 0
                     ? '2px solid rgba(16, 185, 129, 0.3)'
                     : '2px solid rgba(239, 68, 68, 0.3)'
                 }}>
              <div className={`text-4xl font-black mb-2 ${stats.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.total_profit >= 0 ? '+' : ''}{stats.total_profit.toFixed(2)} USDT
              </div>
              <div className="text-sm text-gray-400">Total Profit/Loss</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-xl text-center"
                   style={{
                     background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
                     border: '2px solid rgba(0, 200, 255, 0.3)'
                   }}>
                <div className="text-2xl font-black text-white">{stats.active_trades}</div>
                <div className="text-sm text-gray-400 mt-1">Active</div>
              </div>
              <div className="p-4 rounded-xl text-center"
                   style={{
                     background: 'linear-gradient(135deg, rgba(0, 200, 255, 0.1) 0%, rgba(0, 150, 255, 0.1) 100%)',
                     border: '2px solid rgba(0, 200, 255, 0.3)'
                   }}>
                <div className="text-2xl font-black text-white">{stats.total_completed}</div>
                <div className="text-sm text-gray-400 mt-1">Completed</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trade Detail Modal */}
      {showTradeModal && selectedTrade && (
        <TradeDetailModal
          trade={selectedTrade}
          type="binary"
          onClose={() => {
            setShowTradeModal(false);
            setSelectedTrade(null);
          }}
        />
      )}
    </div>
  );
};

export default BinaryTrade;
